/* eslint-disable @typescript-eslint/no-explicit-any */
import { registerGsap } from "./gsap";
import { getLenis } from "./smooth-scroll";
import { makeParallax } from "./parallax";

/**
 * Footer reveal + parallax settle. Each block reveals when its own container nears
 * the viewport (not when the footer starts). Reverts cleanly on unmount/route change.
 */
export function initFooterMotion(): () => void {
  const { gsap, ScrollTrigger } = registerGsap();
  const footer = document.querySelector<HTMLElement>(".site-footer");
  if (!footer) return () => {};

  const ease = "expo.out";
  const mm = gsap.matchMedia();

  const reveal = () => {
    /* The rail may mount after this runs (inner pages build it client-side): look it up on toggle. */
    ScrollTrigger.create({
      trigger: footer,
      start: "top 80%",
      onToggle: (self: any) => document.getElementById("home-dots")?.classList.toggle("is-away", self.isActive),
    });
    const blocks = [
      { at: ".footer-home-lead", start: "top 82%", sets: [".footer-lead-copy > *", ".footer-snippet"] },
      { at: ".footer-spec", start: "top 90%", sets: [".footer-spec > div"] },
      { at: ".footer-main", start: "top 86%", sets: [".footer-brand > *", ".footer-columns section h4, .footer-columns section a"] },
      { at: ".footer-bottom", start: "top 98%", sets: [".footer-bottom > *"] },
    ];
    blocks.forEach((blk) => {
      const host = footer.querySelector<HTMLElement>(blk.at);
      if (!host) return;
      const lists = blk.sets.map((sel) => host.querySelectorAll(sel)).filter((l) => l.length);
      if (!lists.length) return;
      lists.forEach((list) => gsap.set(list, { autoAlpha: 0, y: 24 }));
      ScrollTrigger.create({
        trigger: host,
        start: blk.start,
        once: true,
        onEnter: () => {
          const tl = gsap.timeline({ defaults: { ease } });
          lists.forEach((list, i) => tl.to(list, { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.04 }, i * 0.1));
        },
      });
    });
  };

  const parallax = (k: number) => {
    const { settle } = makeParallax(k);
    settle(".footer-home-lead", ".footer-lead-copy", 34, "top 38%");
    settle(".footer-home-lead", ".footer-snippet", 78, "top 38%");
    settle(".footer-spec", ".footer-spec > div", [16, 30, 44, 58], "top 62%");
    settle(".footer-main", ".footer-brand", 30, "top 45%");
    settle(".footer-main", ".footer-columns section", [26, 44, 62, 80], "top 45%");
    settle(".footer-wordmark", ".footer-wordmark", 72, "top 88%");
  };

  mm.add("(min-width: 901px)", () => {
    reveal();
    parallax(1);
  });
  mm.add("(max-width: 900px)", () => {
    reveal();
    parallax(0.5);
  });

  return () => mm.revert();
}

/** Copy-handshake button, back-to-top button, current year. */
export function initFooterTools(reduced: boolean): () => void {
  const ac = new AbortController();
  const { signal } = ac;
  let timer = 0;

  const year = document.getElementById("year");
  if (year) year.textContent = String(new Date().getFullYear());

  const copy = document.getElementById("copy-cmd");
  if (copy) {
    copy.addEventListener(
      "click",
      () => {
        const text = copy.getAttribute("data-copy") || "";
        const done = () => {
          copy.textContent = "Copied";
          copy.classList.add("is-done");
          window.clearTimeout(timer);
          timer = window.setTimeout(() => {
            copy.textContent = "Copy";
            copy.classList.remove("is-done");
          }, 1800);
        };
        const legacy = () => {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          try {
            if (document.execCommand("copy")) done();
          } catch {
            /* ignore */
          }
          document.body.removeChild(ta);
        };
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(done, legacy);
        else legacy();
      },
      { signal }
    );
  }

  const top = document.getElementById("to-top");
  if (top) {
    top.addEventListener(
      "click",
      () => {
        const lenis = getLenis();
        if (lenis) lenis.scrollTo(0, { duration: 1.8 });
        else window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
      },
      { signal }
    );
  }

  return () => {
    ac.abort();
    window.clearTimeout(timer);
  };
}
